import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  ArrowDown,
  ArrowUp,
  GraduationCap,
  Minus,
  MousePointerClick,
  PoundSterling,
  Ticket,
  Users,
} from 'lucide-react'

const icons = {
  spend: PoundSterling,
  event_registrations: Ticket,
  courses_completed: GraduationCap,
  roster: Users,
  personal_memberships: Users,
  groups: Users,
  email_clicks: MousePointerClick,
}
const directions = {
  up: { Icon: ArrowUp, label: 'up', className: 'text-success' },
  down: { Icon: ArrowDown, label: 'down', className: 'text-destructive' },
  level: { Icon: Minus, label: 'no change', className: 'text-muted-foreground' },
}

export interface EngagementTileData {
  key: keyof typeof icons
  label: string
  figure: string
  comparison: string | null
  direction: keyof typeof directions | 'none'
  secondary_lines: string[]
}

export function EngagementTile({
  tile,
  compact = false,
  selected = false,
  onSelect,
  showComparison = true,
}: {
  tile: EngagementTileData
  compact?: boolean
  selected?: boolean
  onSelect?: () => void
  showComparison?: boolean
}) {
  const Icon = icons[tile.key]
  const arrow = tile.direction === 'none' ? null : directions[tile.direction]
  const content = (
    <article
      className={`flex min-w-0 flex-col border-border p-4 ${compact && !onSelect ? 'even:border-l [&:nth-child(n+3)]:border-t sm:[&:not(:first-child)]:border-l sm:[&:nth-child(n+3)]:border-t-0' : ''}`}
      aria-label={tile.label}
    >
      <div className="flex h-6 items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon aria-hidden="true" className="h-3.5 w-3.5" />
        </span>
        <span className="min-w-0 flex-1 truncate whitespace-nowrap text-caption text-muted-foreground">
          {tile.label}
        </span>
      </div>
      <div className="mt-3 break-words text-heading-sm font-semibold tabular-nums text-foreground">
        {tile.figure}
      </div>
      {showComparison && (
        <div className="mt-1 flex min-h-5 items-center gap-1 text-caption text-muted-foreground">
          {arrow && (
            <arrow.Icon
              aria-label={arrow.label}
              className={`h-3 w-3 shrink-0 ${arrow.className}`}
            />
          )}
          <span className="min-w-0 break-words">{tile.comparison}</span>
        </div>
      )}
    </article>
  )
  if (!onSelect) return content
  return (
    <Button
      variant="ghost"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'relative h-auto min-w-0 items-stretch justify-start rounded-none p-0 text-left [&>article]:w-full',
        compact &&
          'even:border-l [&:nth-child(n+3)]:border-t sm:[&:not(:first-child)]:border-l sm:[&:nth-child(n+3)]:border-t-0',
        selected &&
          'bg-primary/5 after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary',
      )}
    >
      {content}
    </Button>
  )
}
